import { FC, useEffect, useMemo, useRef } from 'react';
import * as React from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import useDashboardContext from '../context/useDashboardContext';
import { RouteType } from '../types';
import DesktopMenu from './DesktopMenu';
import MobileMenu from './MobileMenu';

export interface DashboardMenuProps {
  routes: RouteType[];
  supportsDarkMode?: boolean;
  isNewHomeUserB2BEnabled?: boolean;
  onRouteChange?: () => void;
}

const DashboardMenu: FC<React.PropsWithChildren<DashboardMenuProps>> = ({
  routes,
  supportsDarkMode,
  children,
  onRouteChange,
  isNewHomeUserB2BEnabled,
}) => {
  const location = useLocation();
  const history = useHistory();
  const mounted = useRef<boolean>(false);
  const { scrollTop } = useDashboardContext();

  const currentIndex = useMemo(() => {
    const firstRoute = routes[0];

    if (location.pathname === '/' && firstRoute) {
      history.push(firstRoute.path);
      return 0;
    }
    const currentPath = location.pathname;

    return routes.findIndex(
      r =>
        r.path.includes(currentPath) ||
        r.subpaths?.find(subpath =>
          `${r.path}${subpath}`.includes(currentPath),
        ),
    );
  }, [location.pathname]);

  useEffect(() => {
    scrollTop();
    if (!mounted.current && location.pathname !== '/') {
      mounted.current = true;
    } else {
      try {
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: 'smooth',
        });
      } catch (e) {
        window.scrollTo(0, 0);
      }
    }
  }, [location.pathname]);

  const onSelect = (value: string | number) => {
    const selectedRoute = routes[value as number];

    if (onRouteChange) {
      onRouteChange();
    } else if (selectedRoute) {
      history.push(selectedRoute.path);
    }
  };

  return (
    <>
      <MobileMenu
        routes={routes}
        currentIndex={currentIndex}
        supportsDarkMode={supportsDarkMode}
        onSelect={onSelect}
      />
      <DesktopMenu
        routes={routes}
        currentIndex={currentIndex}
        supportsDarkMode={supportsDarkMode}
        isNewHomeUserB2BEnabled={isNewHomeUserB2BEnabled}
        onSelect={onSelect}
      >
        {children}
      </DesktopMenu>
    </>
  );
};

export default DashboardMenu;
